import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, of, from, Subject } from 'rxjs';
import { map, switchMap, take, takeUntil } from 'rxjs/operators';
import { Platform } from '@ionic/angular';
import {
  getAutoprintQueue,
  getAutoprintSubscriptions,
  getAutoprintSubscriptionsByStoreId,
} from './+state/printers.selectors';
import {
  AutoprintQueueElement,
  AutoprintSubscriptions,
  PrinterInfo,
} from './printers';
import {
  UpdateAutoprintQueue,
  UpdateAutoprintSubscriptions,
} from './+state/printers.actions';
import { OrderPdfResponse } from '../stores/store-order/store-order';
import { HttpClient, HttpResponse } from '@angular/common/http';
import EscPosEncoder from 'esc-pos-encoder-ionic';
import { STARPRINTER_EMULATION } from './star';
import { ToastrService } from 'ngx-toastr';
import { GoToast } from 'src/app/shared/go-toast.component';
import { LogService } from '../shared/logging/LogService';
import { HelperService } from '../public/helper.service';
import { Printer } from '@awesome-cordova-plugins/printer/ngx';
import { MobileFileService } from '../shared/mobile.file.service';
import { BluetoothLeService } from './bluetoothLe.service';
import { Capacitor } from '@capacitor/core';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare var Socket: any;

@Injectable({
  providedIn: 'root',
})
export class PrinterService implements OnDestroy {
  subscriptions: AutoprintSubscriptions;
  autoPrintQueue$: Observable<AutoprintQueueElement[]>;
  printTaskCompleted = true;
  private autoprintQueueDestroy$ = new Subject<void>();
  private autoprintQueueInterval: ReturnType<typeof setInterval> | null = null;
  airPrinter: Printer;

  constructor(
    private store: Store,
    private platform: Platform,
    private http: HttpClient,
    private toastr: ToastrService,
    private logger: LogService,
    private helperService: HelperService,
    private bluetoothLeService: BluetoothLeService,
    private mobileFileService: MobileFileService,
  ) {
    this.airPrinter = new Printer();

    this.subscriptions = {};
    this.autoPrintQueue$ = this.store.select(getAutoprintQueue);
    // Track interval to clear on destroy
    this.autoprintQueueInterval = setInterval(() => {
      this.store
        .select(getAutoprintQueue)
        .pipe(
          take(1), // Use take(1) instead of takeUntil to avoid subscription accumulation
        )
        .subscribe(async (autoprintQueue) => {
          if (
            !!autoprintQueue &&
            autoprintQueue.length > 0 &&
            this.printTaskCompleted
          ) {
            const autoprintElem = autoprintQueue[0];
            autoprintQueue[0].status = 'INPROGRESS';
            this.store.dispatch(new UpdateAutoprintQueue(autoprintQueue));
            this.printTaskCompleted = false;
            this.autoPrint(
              autoprintElem.printer,
              autoprintElem.storeId,
              autoprintElem.orderId,
            )
              .toPromise()
              .then((res) => {
                this.removePrintFromQueue(
                  autoprintQueue,
                  autoprintElem.orderId,
                );
                this.printTaskCompleted = true;
                return of(res);
              })
              .catch((err) => {
                this.removePrintFromQueue(
                  autoprintQueue,
                  autoprintElem.orderId,
                );
                this.printTaskCompleted = true;
              });
          }
        });
    }, 5000);

    this.platform.ready().then(() => {
      this.bluetoothLeService.initialize();
    });
  }

  private removePrintFromQueue(
    autoprintQueue: AutoprintQueueElement[],
    orderId: string,
  ) {
    const printIndex = autoprintQueue.findIndex(
      (autoprint) => autoprint.orderId == orderId,
    );
    if (printIndex >= 0) {
      autoprintQueue.splice(printIndex, 1);
      this.store.dispatch(new UpdateAutoprintQueue(autoprintQueue));
    }
  }

  isSupported() {
    // need some investigation...
    return true;
  }

  init(subscriptions: AutoprintSubscriptions): void {
    this.subscriptions = { ...subscriptions };
  }

  addSubscription(
    storeId: number,
    subscriptions: AutoprintSubscriptions,
  ): AutoprintSubscriptions {
    this.init(subscriptions);
    if (!Object.prototype.hasOwnProperty.call(this.subscriptions, storeId)) {
      this.subscriptions[`${storeId}`] = true;
      this.store.dispatch(new UpdateAutoprintSubscriptions(this.subscriptions));
    } else {
    }
    return this.subscriptions;
  }

  removeSubscription(
    storeId: number,
    subscriptions: AutoprintSubscriptions,
  ): AutoprintSubscriptions {
    this.subscriptions = { ...subscriptions };
    if (Object.prototype.hasOwnProperty.call(this.subscriptions, storeId)) {
      delete this.subscriptions[`${storeId}`];

      this.clearAutoPrintQueue(storeId);
      this.store.dispatch(new UpdateAutoprintSubscriptions(this.subscriptions));
    } else {
    }
    return this.subscriptions;
  }

  requestPermission(): Observable<boolean> {
    if (this.isAndroid()) {
      return of(true);
    } else if (this.isIOS()) {
      return from(this.airPrinter.isAvailable());
    } else {
      return of(false);
    }
  }

  printContentUrlToAirPrintPrinter(contentUrl: string, printerUrl: string) {
    return from(this.airPrinter.print(contentUrl, { printer: printerUrl }));
  }

  isAndroid() {
    return this.helperService.isAndroid();
  }

  isIOS() {
    return Capacitor.getPlatform() === 'ios';
  }

  printContentUrl(contentUrl: string, printer: PrinterInfo) {
    const printerUrl = printer.url;
    const type = printer.type;

    if (type == 'AIRPRINT') {
      return this.printContentUrlToAirPrintPrinter(contentUrl, printerUrl);
    }

    return of(null);
  }

  saveFile(blob: Blob, fileName: string) {
    return from(this.mobileFileService.writeFileOnDevice(fileName, blob));
  }

  addToAutoPrintQueue(
    printer: PrinterInfo,
    storeId: number,
    orderId: string,
  ): Observable<boolean> {
    this.autoPrintQueue$.pipe(take(1)).subscribe((autoprintQueue) => {
      if (!autoprintQueue) {
        autoprintQueue = [];
      }
      autoprintQueue.push({
        storeId: storeId,
        orderId: orderId,
        printer: printer,
        status: 'NOTSTARTED',
      });
      this.store.dispatch(new UpdateAutoprintQueue(autoprintQueue));
    });
    return of(true);
  }

  clearAutoPrintQueue(storeId: number) {
    this.autoPrintQueue$.pipe(take(1)).subscribe((autoprintQueue) => {
      const updatedAutoprintQueue = autoprintQueue.filter(
        (elem) => elem.storeId !== storeId,
      );
      this.store.dispatch(new UpdateAutoprintQueue(updatedAutoprintQueue));
    });
    return of(true);
  }

  autoPrint(
    printer: PrinterInfo,
    storeId: number,
    orderId: string,
  ): Observable<OrderPdfResponse> {
    return this.http
      .get(`/api/v1/stores/${storeId}/orders/${orderId}/invoice?toImage=true`, {
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(
        take(1),
        switchMap(async (r) => {
          let blobResObj = this.toOrderCodeResponse(r as HttpResponse<Blob>);
          const socket = new Socket();
          const encoder = new EscPosEncoder();
          const result = encoder.initialize();
          let resultByte = null;

          let img = new Image();
          const blobUrl = URL.createObjectURL(blobResObj.blob);
          img.src = blobUrl;

          const self = this;

          const loader = new Promise((resolve) => {
            img.onload = function () {
              const canvas = document.createElement('canvas');
              const aspectRatio = img.height / img.width;
              canvas.width = printer.paperWidth * 3.5 * 2;
              canvas.height = aspectRatio * canvas.width;
              const ctx = canvas.getContext('2d');
              ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
              const data = ctx.canvas.toDataURL();
              resultByte = encoder
                .newline()
                .newline()
                .newline()
                .image(
                  canvas,
                  8 * Math.floor(canvas.width / 8),
                  8 * Math.floor(canvas.height / 8),
                  'atkinson',
                  1,
                )
                .newline()
                .newline()
                .newline()
                .newline()
                .newline()
                .newline()
                .encode();

              // Cleanup: revoke blob URL and remove image reference
              URL.revokeObjectURL(blobUrl);
              img.onload = null;
              img.src = '';

              switch (printer.mode) {
                case 'WiFi/Ethernet':
                  socket.open(
                    printer.ip,
                    printer.port,
                    () => {
                      socket.write(resultByte, () => {
                        socket.shutdownWrite();
                        self.toastr.success(
                          null,
                          `Printed Successfully for Order Uuid - ` + orderId,
                          {
                            toastComponent: GoToast,
                          },
                        );
                        resolve((self.printTaskCompleted = true));
                      });
                    },
                    (err) => {
                      self.toastr.error(
                        null,
                        `AutoPrint WiFi Error : ` + JSON.stringify(err),
                        {
                          toastComponent: GoToast,
                        },
                      );
                      resolve((self.printTaskCompleted = true));
                    },
                  );
                  break;
                case 'Bluetooth':
                  self.bluetoothLeService
                    .print(printer.macAddr, resultByte, printer.speed)
                    .then(
                      () => {
                        self.toastr.success(
                          null,
                          `Printed Successfully for Order Uuid - ` + orderId,
                          {
                            toastComponent: GoToast,
                          },
                        );
                        resolve((self.printTaskCompleted = true));
                      },
                      (err) => {
                        self.toastr.error(
                          null,
                          `AutoPrint Bluetooth Error : ` + JSON.stringify(err),
                          {
                            toastComponent: GoToast,
                          },
                        );
                        resolve((self.printTaskCompleted = true));
                      },
                    );
                  break;
              }
            };
          });

          // Error handler to cleanup on failure
          img.onerror = function () {
            URL.revokeObjectURL(blobUrl);
            img.onload = null;
            img.onerror = null;
            img.src = '';
          };

          await loader
            .then((res) => {})
            .catch((err) => {
              // Cleanup on error
              URL.revokeObjectURL(blobUrl);
              img.onload = null;
              img.onerror = null;
              img.src = '';
              this.logger.error(err);
              this.printTaskCompleted = true;
            });
          return blobResObj;
        }),
      );
  }

  testPrint(printer: PrinterInfo): Promise<boolean> {
    const printerMode = printer.mode;
    const printerName = printer.name;
    const connectionParamsIp = printer.ip;
    const connectionParamsPort = printer.port;
    const connectionParamsBluetooth = printer.macAddr;
    const connectionParamsPaperWidthInMM = printer.paperWidth;
    const connectionParamsBluetoothSpeed = printer.speed;

    return new Promise((resolve) => {
      const socket = new Socket();
      const self = this;
      let img = new Image();
      img.src = 'assets/printer-test-image.png';
      const encoder = new EscPosEncoder();

      const testSuccessfulText =
        printerMode === 'Bluetooth'
          ? 'Bluetooth MAC : ' + connectionParamsBluetooth
          : 'IP : ' + connectionParamsIp + '  Port : ' + connectionParamsPort;
      const resultByte: Uint8Array = encoder
        .align('center')
        .line('Congratulation, ' + printerName + ' print success')
        .line(testSuccessfulText)
        .newline()
        .newline()
        .newline()
        .newline()
        .newline()
        .newline()
        .encode();

      const testImageLoader = new Promise((resolveImageLoad) => {
        img.onload = function () {
          const canvas = document.createElement('canvas');
          const aspectRatio = img.height / img.width;
          canvas.width = connectionParamsPaperWidthInMM * 3.5 * 2;
          canvas.height = aspectRatio * canvas.width;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

          // Cleanup image after use
          img.onload = null;
          img.onerror = null;
          img.src = '';

          resolveImageLoad(
            encoder
              .newline()
              .newline()
              .newline()
              .image(
                canvas,
                8 * Math.floor(canvas.width / 8),
                8 * Math.floor(canvas.height / 8),
                'atkinson',
                1,
              )
              .newline()
              .newline()
              .newline()
              .newline()
              .newline()
              .newline()
              .encode(),
          );
        };

        img.onerror = function () {
          img.onload = null;
          img.onerror = null;
          img.src = '';
        };
      });

      testImageLoader
        .then((resultByteImage: Uint8Array) => {
          if (!resultByteImage) {
            this.log('Unable to Load Test Image!');
            resolve(false);
          }
          this.log('Trying to Print...');

          if (printerMode === 'Bluetooth') {
            this.bluetoothLeService
              .print(
                connectionParamsBluetooth,
                resultByte,
                connectionParamsBluetoothSpeed,
              )
              .then((printed) => {
                if (printed) {
                  this.log('Printer Test - Text Print Successful!');
                }

                this.bluetoothLeService
                  .print(
                    connectionParamsBluetooth,
                    resultByteImage,
                    connectionParamsBluetoothSpeed,
                  )
                  .then((printed) => {
                    if (printed) {
                      this.log('Printer Test - Image Print Successful!');
                      resolve(true);
                    } else {
                      resolve(false);
                    }
                  })
                  .catch((err) => {
                    this.logger.error(
                      'Printer Connection Error : ' + JSON.stringify(err),
                    );
                    resolve(false);
                  });
              })
              .catch((err) => {
                this.logger.error(
                  'Printer Image Connection Error: ' + JSON.stringify(err),
                );
                resolve(false);
              });
          } else {
            socket.onError = (err) => {
              this.logger.error(JSON.stringify(err));
              socket.close();
            };
            socket.open(
              connectionParamsIp,
              connectionParamsPort,
              () => {
                socket.write(
                  resultByte,
                  () => {
                    this.log('Printer Test - Text Print Successful!');
                  },
                  (err) => {
                    this.logger.error(
                      'Printer Test - Text Print Failed!' + JSON.stringify(err),
                    );
                    resolve(false);
                  },
                );
                setTimeout(() => {
                  socket.write(
                    resultByteImage,
                    () => {
                      socket.shutdownWrite();
                      this.log('Printer Test - Image Print Successful!');
                      resolve(true);
                    },
                    (err) => {
                      this.logger.error(
                        'Printer Test - Image Print Failed!' +
                          JSON.stringify(err),
                      );
                      resolve(false);
                    },
                  );
                }, 500);
              },
              (err) => {
                this.logger.error(
                  'Printer Test Failed! : ' + JSON.stringify(err),
                );
                socket.close();
                resolve(false);
              },
            );
          }
        })
        .catch((err) => {
          this.logger.error(
            'Printer Test Failed While Loading Test Image : ' +
              JSON.stringify(err),
          );
          resolve(false);
        });
    });
  }

  private toOrderCodeResponse(response: HttpResponse<Blob>): OrderPdfResponse {
    const blob = response.body;
    const contentDisposition = response.headers.get('content-disposition');
    const filename = contentDisposition
      .slice(contentDisposition.indexOf('filename=') + 9)
      .replace(/"/g, '');
    return { blob, filename };
  }

  private log(msg) {
    this.toastr.error(null, msg, { toastComponent: GoToast });
  }

  ngOnDestroy() {
    // Clear interval to prevent memory leaks
    if (this.autoprintQueueInterval !== null) {
      clearInterval(this.autoprintQueueInterval);
      this.autoprintQueueInterval = null;
    }

    // Complete subject
    this.autoprintQueueDestroy$.next();
    this.autoprintQueueDestroy$.complete();
  }
}
